import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import SideNav from "./components/hostComponents/SideNav";
import ErrorBoundary from "./components/ErrorBoundary";
import {
  LoginScreen,
  SignupScreen,
  HostScreen,
  AddPropertyScreen,
  ProfileSettingScreen,
  PropertyDetailsScreen,
  PleaseVerifyEmailScreen,
  VerificationScreen,
  ForgetPasswordScreen,
  ChangePasswordScreen,
  Admin,
  DriverStripeRedirectScreen,
  ConciergeScreen,
} from "./screens";
import HostDashboard from "./screens/HostDashboard";
import SupportScreen from "./screens/SupportScreen";
import DriverEmailVerificationScreen from "./screens/DriverEmailVerificationScreen";
import OrdersScreen from "./screens/OrdersScreen";
import OrderDetailsScreen from "./screens/OrderDetailsScreen";
import PageNotFoundScreen from "./screens/PageNotFoundScreen";
import PrivateRoutes from "./components/utilityComponents/PrivateRoutes";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";
import PasswordResetSuccessful from "./screens/PasswordResetSuccessful";
import ChangeEmailScreen from "./screens/ChangeEmailScreen";
import ChangeEmailVerificationScreen from "./screens/ChangeEmailVerificationScreen";
import MessageCentreScreen from "./screens/MessageCentreScreen";
import SyncGuestyProperties from "./screens/SyncGuestyProperties";
import AffiliateSignupScreen from './screens/AffiliateSignupScreen';
import AffiliateLandingPage from "./screens/AffiliateLandingPage";
import PMSLandingPage from "./screens/PMSLandingPage";
import RequireOta from "./utils/requireOta";

// Lazy load heavy screens to reduce initial bundle size and prevent OOM
const BellhoptPlatinumScreen = lazy(() => import("./screens/BellhoptPlatinumScreen"));
const Email = lazy(() => import("./screens/Email")); // Has large SVG imports (18MB+ files)

const App = () => {

  // Layout for authenticated routes with sidebar
  const PrivateLayout = ({ children }) => (
    <div className="min-h-screen bg-gray-50">
      <div className="flex w-full min-h-screen">
        <SideNav />
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );

  // Layout for public routes without sidebar
  const PublicLayout = ({ children }) => (
    <div className="min-h-screen bg-gray-50">
      {children}
    </div>
  );

  return (
    <Routes>
      {/* Private routes with sidebar */}
      <Route element={<PrivateRoutes />}>
        <Route path="/host" element={<PrivateLayout><ErrorBoundary><HostScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/concierge" element={<PrivateLayout><RequireOta><ConciergeScreen /></RequireOta></PrivateLayout>} />
        <Route path="addProperty" element={<PrivateLayout><ErrorBoundary><AddPropertyScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/profileSetting/:hid" element={<PrivateLayout><ErrorBoundary><ProfileSettingScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/host/propertyDetails/:pid" element={<PrivateLayout><ErrorBoundary><PropertyDetailsScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/hostdashboard" element={<PrivateLayout><ErrorBoundary><HostDashboard /></ErrorBoundary></PrivateLayout>} />
        <Route path="/sync-guesty-properties" element={<PrivateLayout><ErrorBoundary><SyncGuestyProperties /></ErrorBoundary></PrivateLayout>} />
        <Route path="/orderList" element={<PrivateLayout><ErrorBoundary><OrdersScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/admin" element={<PrivateLayout><ErrorBoundary><Admin /></ErrorBoundary></PrivateLayout>} />
        <Route path="/profileSetting/:hid/reset-password" element={<PrivateLayout><ErrorBoundary><ResetPasswordScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/profileSetting/:hid/change-email" element={<PrivateLayout><ErrorBoundary><ChangeEmailScreen /></ErrorBoundary></PrivateLayout>} />
      <Route path="/messageCentre" element={<PrivateLayout><ErrorBoundary><MessageCentreScreen /></ErrorBoundary></PrivateLayout>} />
      <Route path="/email" element={<PrivateLayout><ErrorBoundary><Suspense fallback={<div>Loading...</div>}><Email /></Suspense></ErrorBoundary></PrivateLayout>} />
      </Route>

      {/* Public routes without sidebar */}
      <Route path='/' element={<PublicLayout><LoginScreen /></PublicLayout>} />
      <Route path="/platinum" element={<PublicLayout><Suspense fallback={<div>Loading...</div>}><BellhoptPlatinumScreen /></Suspense></PublicLayout>} />
      <Route path="/login" element={<PublicLayout><LoginScreen /></PublicLayout>} />
      <Route path="/signup" element={<PublicLayout><SignupScreen /></PublicLayout>} />
      <Route path="/signup/affiliate" element={<PublicLayout><AffiliateSignupScreen /></PublicLayout>} />
      <Route path="/signup/affiliate/:code" element={<PublicLayout><AffiliateSignupScreen /></PublicLayout>} />
      <Route path="/pms/:pmsName" element={<PublicLayout><PMSLandingPage /></PublicLayout>} />
      <Route path="/please-verify" element={<PublicLayout><PleaseVerifyEmailScreen /></PublicLayout>} />
      <Route path="/verify-email/:token" element={<PublicLayout><VerificationScreen /></PublicLayout>} />
      <Route path="/login/forgotPassword" element={<PublicLayout><ForgetPasswordScreen /></PublicLayout>} />
      <Route path="/resetPassword/:token" element={<PublicLayout><ChangePasswordScreen host={true} /></PublicLayout>} />
      <Route path="/password-reset/successful" element={<PublicLayout><PasswordResetSuccessful /></PublicLayout>} />
      <Route path="/support" element={<PublicLayout><SupportScreen /></PublicLayout>} />
      <Route path='/update-email-confirmation/:token' element={<PublicLayout><ChangeEmailVerificationScreen /></PublicLayout>} />
      <Route path="/affiliate-info" element={<PublicLayout><AffiliateLandingPage /></PublicLayout>} />

      {/* DRIVER ROUTES */}
      <Route path="/driver/verify-email/:token" element={<PublicLayout><DriverEmailVerificationScreen /></PublicLayout>} />
      <Route path="/driver/resetPassword/:token" element={<PublicLayout><ChangePasswordScreen driver={true} /></PublicLayout>} />
      <Route path="/driver/stripe" element={<PublicLayout><DriverStripeRedirectScreen /></PublicLayout>} />

      {/* Entry Instructions Submission (public route, no auth required - uses JWT token from email) */}
      <Route path="/order-details/:orderId" element={<PublicLayout><ErrorBoundary><OrderDetailsScreen /></ErrorBoundary></PublicLayout>} />

      {/* Not Found Route */}
      <Route path="*" element={<PublicLayout><PageNotFoundScreen /></PublicLayout>} />
    </Routes>
  );
};

export default App;
